import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ChatProvider } from './hooks/useChat';
import { DashboardProvider } from './hooks/useDashboard';
import { PinnedChartsProvider } from './hooks/usePinnedCharts';
import { SettingsProvider } from './hooks/useSettings';
import { ChatHistoryProvider } from './hooks/useChatHistory';
import Sidebar from './components/Sidebar';
import ChatArea from './components/ChatArea';
import Dashboard from './components/Dashboard';
import FeedbackDashboard from './components/FeedbackDashboard';
import DebugDashboard from './components/DebugDashboard';
import LoginPage from './components/LoginPage';
import ErrorBoundary from './components/ErrorBoundary';
import { GlobalStyles } from './styles/GlobalStyles';
import { clearAuth } from './services/api';

const AppContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  font-family: 'Inter', 'Noto Sans Arabic', sans-serif;
`;

// Keep components mounted but hidden for instant tab switching
const TabPanel = styled.div`
  display: ${props => props.$active ? 'flex' : 'none'};
  flex: 1;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

function App() {
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'dashboard'
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');

  // Check if user is already logged in on mount
  useEffect(() => {
    const storedAuth = localStorage.getItem('isAuthenticated');
    const storedUsername = localStorage.getItem('username');
    if (storedAuth === 'true' && storedUsername) {
      setIsAuthenticated(true);
      setUsername(storedUsername);
    }
  }, []);

  const handleLogin = (loggedInUsername) => {
    setIsAuthenticated(true);
    setUsername(loggedInUsername);
  };

  const handleLogout = () => {
    clearAuth();  // Clear all auth data (tokens, user info)
    setIsAuthenticated(false);
    setUsername('');
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <ErrorBoundary>
        <GlobalStyles />
        <LoginPage onLogin={handleLogin} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <SettingsProvider>
        <ChatHistoryProvider>
          <ChatProvider>
            <DashboardProvider>
              <PinnedChartsProvider>
                <GlobalStyles />
                <AppContainer>
                  <ErrorBoundary>
                    <Sidebar
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                      username={username}
                      onLogout={handleLogout}
                    />
                  </ErrorBoundary>
                  <TabPanel $active={activeTab === 'chat'}>
                    <ErrorBoundary>
                      <ChatArea />
                    </ErrorBoundary>
                  </TabPanel>
                  <TabPanel $active={activeTab === 'dashboard'}>
                    <ErrorBoundary>
                      <Dashboard />
                    </ErrorBoundary>
                  </TabPanel>
                  <TabPanel $active={activeTab === 'feedback'}>
                    <ErrorBoundary>
                      <FeedbackDashboard isActive={activeTab === 'feedback'} />
                    </ErrorBoundary>
                  </TabPanel>
                  <TabPanel $active={activeTab === 'debug'}>
                    <ErrorBoundary>
                      <DebugDashboard isActive={activeTab === 'debug'} />
                    </ErrorBoundary>
                  </TabPanel>
                </AppContainer>
              </PinnedChartsProvider>
            </DashboardProvider>
          </ChatProvider>
        </ChatHistoryProvider>
      </SettingsProvider>
    </ErrorBoundary>
  );
}

export default App;
